import React, { useState } from 'react';
import { toast } from 'sonner';
import { api, enc, useApi } from '../../api';
import { Field, Toggle, BackupsField, InstantToggle } from '../../ui';
import { PlanLock } from '../../Upgrade';
import { useShell } from '../../Shell';
import { RouteSummary, TestNumber } from './RouteSummary';
import { SipCredentials } from './SipTab';
import { VoiceMaker } from '../../VoiceMaker';
import { useUnsaved, SaveRow } from '../../guard';

const VM_TEMPLATES = [
  ['Standard', 'Hi, you have reached [Name]. I can’t take your call right now. Please leave a message after the tone.'],
  ['Business', 'Thank you for calling [Business]. All of our team members are busy. Please leave your name, number and a short message and we will call you back.'],
  ['After hours', 'Thanks for calling [Business]. We are closed right now. Please leave a message and we will return your call on the next business day.'],
];

// Shared save helper for the simple PATCH settings forms.
// okText (optional): the success message, or a function of the server reply — e.g. to say what else the save switched off.
export function useSave(num, path, reload) {
  const [busy, setBusy] = useState(false);
  const save = async (body, method = 'PATCH', okText) => {
    setBusy(true);
    try {
      const res = await api(`/numbers/${enc(num.phoneNumber)}/${path}`, { method, body });
      toast.success((typeof okText === 'function' ? okText(res) : okText) || 'Saved');
      reload(); return true;
    } catch (e) { toast.error(e.message); return false; } finally { setBusy(false); }
  };
  return [save, busy];
}

export const Card = ({ title, sub, children, bind }) => (
  <div className="civr-card p-6 max-w-2xl" {...bind}>
    <div className="font-display text-2xl font-bold">{title}</div>
    {sub && <p className="text-sm text-[#a1a1aa] mt-1 mb-6">{sub}</p>}
    <div className="space-y-6">{children}</div>
  </div>
);

function Meter({ label, used, total, testid }) {
  const unlimited = total === 'Unlimited' || total === null || total === undefined;
  const pct = unlimited ? 0 : Math.min(100, (Number(used) / Number(total || 1)) * 100);
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-sm"><span className="text-[#d4d4d8]">{label}</span><span className="font-mono">{used} / {unlimited ? '∞' : total}</span></div>
      <div className="h-2 rounded-full bg-[#1c1c1c] mt-2 overflow-hidden"><div className="h-full bg-[#818cf8] rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function OverviewTab({ num, reload }) {
  const caps = num.capabilities || {};
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <RouteSummary num={num} reload={reload} />
      <TestNumber num={num} />
      {num.access?.sipCredentials && <SipCredentials num={num} prefix="overview-sip" heading="SIP login" />}
      <div className="civr-card p-6 lg:col-span-2 space-y-6">
        <div className="font-display text-2xl font-bold">This month</div>
        <Meter label="Inbound minutes" used={num.usage.minutesUsed} total={num.usage.minutesIncluded} testid="overview-minutes-meter" />
        <Meter label="SMS" used={num.usage.smsUsed} total={num.usage.smsIncluded} testid="overview-sms-meter" />
        {num.usage.shared && <div className="text-xs text-[#a1a1aa]" data-testid="overview-usage-shared-note">Shared allowance — totals include your main number and all its extra numbers.</div>}
      </div>
      <div className="civr-card p-6 space-y-3 text-sm" data-testid="overview-capabilities">
        <div className="font-display text-2xl font-bold mb-2">Capabilities</div>
        {[['voice', 'Voice'], ['sms', 'SMS'], ['fax', 'Fax']].map(([c, l]) => <div key={c} className="flex justify-between"><span className="text-[#d4d4d8]">{l}</span><span className={caps[c] ? 'text-[#818cf8]' : 'text-[#a1a1aa]'} data-testid={`overview-capability-${c}`}>{caps[c] ? 'Yes' : 'No'}</span></div>)}
        <div className="flex justify-between"><span className="text-[#d4d4d8]">SIP domain</span><span className="font-mono text-xs">{num.sipDomain}</span></div>
      </div>
    </div>
  );
}

export function ForwardingTab({ num, reload }) {
  const cf = num.features.callForwarding;
  const [f, setF] = useState({ mode: cf.mode || 'disabled', forwardTo: cf.forwardTo || '', ringTimeout: cf.ringTimeout || 25, holdMusic: !!cf.holdMusic, backups: cf.backups || [] });
  const [save, busy] = useSave(num, 'forwarding', reload);
  const u = useUnsaved(f);
  return (
    <Card title="Call forwarding" sub="Send calls to any phone. Forwarded minutes bill from your wallet." bind={u.bind}>
      <Field label="When to forward">
        <select className="civr-input" value={f.mode} onChange={e => setF({ ...f, mode: e.target.value })} data-testid="forwarding-mode-select">
          <option value="disabled">Off</option><option value="always">Always</option><option value="busy">When busy</option><option value="no_answer">When not answered</option>
        </select>
      </Field>
      {f.mode !== 'disabled' && <>
        <Field label="Forward to" hint="Include the country code, e.g. +14155550100."><input className="civr-input font-mono" value={f.forwardTo} onChange={e => setF({ ...f, forwardTo: e.target.value })} placeholder="+14155550100" data-testid="forwarding-number-input" /></Field>
        <Field label="Ring time before forwarding (seconds)"><input type="number" min={10} max={60} className="civr-input" value={f.ringTimeout} onChange={e => setF({ ...f, ringTimeout: e.target.value })} data-testid="forwarding-ring-timeout-input" /></Field>
        {f.mode !== 'disabled' && <BackupsField value={f.backups} onChange={v => setF({ ...f, backups: v })} testid="forwarding-backups-input" />}
        <div className="flex items-center justify-between"><span className="text-sm">Play hold music while connecting</span><Toggle checked={f.holdMusic} onChange={v => setF({ ...f, holdMusic: v })} testid="forwarding-hold-music-toggle" /></div>
      </>}
      <SaveRow dirty={u.dirty} testid="forwarding-unsaved-pill"><button className="civr-btn civr-btn-primary" disabled={busy} onClick={async () => { if (await save(f)) u.reset(); }} data-testid="forwarding-save-button">Save forwarding</button></SaveRow>
    </Card>
  );
}

export function VoicemailTab({ num, reload }) {
  const vm = num.features.voicemail;
  const { data, reload: reloadAudio } = useApi('/audio');
  const { catalog } = useShell();
  const [script, setScript] = useState('');
  const langs = catalog?.voicemailLanguages || [{ code: 'en', label: 'English' }];
  const [f, setF] = useState({ greetingType: vm.greetingType === 'custom' ? 'custom' : 'default', customGreetingUrl: vm.customGreetingUrl || '', forwardToEmail: vm.forwardToEmail || '', ringTimeout: vm.ringTimeout || 25, language: vm.language || 'en' });
  const [save, busy] = useSave(num, 'voicemail', reload);
  const u = useUnsaved(f);
  const sample = langs.find(l => l.code === f.language)?.sample;
  return (
    <Card title="Voicemail" sub="Unanswered callers can leave a message. Recordings land in your inbox." bind={u.bind}>
      <div className="flex items-center justify-between"><span className="text-sm">Voicemail on</span><InstantToggle num={num} feature="voicemail" label="Voicemail" checked={!!vm.enabled} reload={reload} testid="voicemail-enabled-toggle" /></div>
      <Field label="Greeting">
        <select className="civr-input" value={f.greetingType} onChange={e => setF({ ...f, greetingType: e.target.value })} data-testid="voicemail-greeting-type-select">
          <option value="default">Standard greeting</option><option value="custom">From my audio library</option>
        </select>
      </Field>
      <Field label="Greeting language" hint={f.greetingType === 'custom' ? 'Your own audio plays as recorded. The language is still used for the after-hours message.' : 'Also used for the after-hours message (Hours tab).'}>
        <select className="civr-input" value={f.language} onChange={e => setF({ ...f, language: e.target.value })} data-testid="voicemail-language-select">
          {langs.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        {f.greetingType === 'default' && sample && <span className="block text-sm text-[#e4e4e7] italic mt-2" dir="auto" data-no-translate data-testid="voicemail-language-sample">“{sample}”</span>}
      </Field>
      {f.greetingType === 'custom' && (
        <Field label="Greeting audio" hint={!data?.audios?.length ? 'Add audio in the Audio library first.' : null}>
          <select className="civr-input" value={f.customGreetingUrl} onChange={e => setF({ ...f, customGreetingUrl: e.target.value })} data-testid="voicemail-greeting-audio-select">
            <option value="">Pick audio…</option>{(data?.audios || []).map(a => <option key={a.id} value={a.audioUrl}>{a.name}</option>)}
          </select>
        </Field>
      )}
      <div className="border border-[#222] rounded-xl p-4 space-y-3" data-testid="voicemail-tts-box">
        <div className="text-sm font-medium">Make a greeting from text</div>
        <div className="flex flex-wrap gap-1.5">{VM_TEMPLATES.map(([l, t], i) => <button key={l} type="button" onClick={() => setScript(t)} className="civr-pill cursor-pointer" data-testid={`voicemail-template-${i}`}>{l}</button>)}</div>
        <textarea rows={3} className="civr-input" value={script} onChange={e => setScript(e.target.value)} placeholder="Type what callers hear before the beep" data-testid="voicemail-tts-text-input" />
        <VoiceMaker text={script} name="Voicemail greeting" onMade={(a) => { reloadAudio(); setF(x => ({ ...x, greetingType: 'custom', customGreetingUrl: a.audioUrl })); }} testid="voicemail-tts" />
        <p className="text-xs text-[#a1a1aa]">Replace [Name] / [Business] before making the voice. The new audio is picked as your greeting — press Save voicemail.</p>
      </div>
      <Field label="Also email voicemails to (optional)"><input className="civr-input" type="email" value={f.forwardToEmail} onChange={e => setF({ ...f, forwardToEmail: e.target.value })} data-testid="voicemail-email-input" /></Field>
      <Field label="Ring time before voicemail (seconds)"><input type="number" min={10} max={60} className="civr-input" value={f.ringTimeout} onChange={e => setF({ ...f, ringTimeout: e.target.value })} data-testid="voicemail-ring-timeout-input" /></Field>
      <div className="flex items-start justify-between gap-4">
        <div><div className="text-sm">Transcribe voicemails</div><div className="text-xs text-[#a1a1aa] mt-1">The message as text in your inbox, email and call logs.</div></div>
        <InstantToggle num={num} feature="transcribe" label="Voicemail transcripts" checked={vm.transcribe !== false} reload={reload} testid="voicemail-transcribe-toggle" />
      </div>
      <SaveRow dirty={u.dirty} testid="voicemail-unsaved-pill"><button className="civr-btn civr-btn-primary" disabled={busy} onClick={async () => { if (await save({ ...f, enabled: !!vm.enabled, transcribe: vm.transcribe !== false })) u.reset(); }} data-testid="voicemail-save-button">Save voicemail</button></SaveRow>
    </Card>
  );
}

function FaxCard({ num, reload }) {
  const [save, busy] = useSave(num, 'fax', reload);
  if (!num.capabilities?.fax) return null;
  return (
    <Card title="Fax" sub="This number can receive faxes. Each fax arrives in your Inbox as a PDF.">
      <div className="flex items-center justify-between"><span className="text-sm">Send faxes to my Inbox</span><Toggle checked={num.features.fax?.toInbox !== false} disabled={busy} onChange={v => save({ toInbox: v })} testid="fax-inbox-toggle" /></div>
    </Card>
  );
}

export function SmsTab({ num, reload }) {
  return <div className="space-y-4"><SmsCard num={num} reload={reload} /><FaxCard num={num} reload={reload} /></div>;
}

function SmsCard({ num, reload }) {
  const s = num.features.smsForwarding;
  const [f, setF] = useState({ toEmail: s.toEmail || '', webhookUrl: s.webhookUrl || '' });
  const [save, busy] = useSave(num, 'sms', reload);
  const u = useUnsaved(f);
  if (!num.capabilities?.sms) return <Card title="SMS" sub="This number can’t receive text messages." />;
  return (
    <Card title="SMS delivery" sub="Inbound texts always appear in your inbox. Add email or a webhook too (Pro and up)." bind={u.bind}>
      <Field label="Email copy (optional)"><input className="civr-input" type="email" value={f.toEmail} disabled={!num.access.smsToEmail} onChange={e => setF({ ...f, toEmail: e.target.value })} data-testid="sms-email-input" /></Field>
      <Field label="Webhook URL (optional)" hint="We POST each message as JSON."><input className="civr-input font-mono" value={f.webhookUrl} disabled={!num.access.smsWebhook} onChange={e => setF({ ...f, webhookUrl: e.target.value })} placeholder="https://" data-testid="sms-webhook-input" /></Field>
      {!num.access.smsToEmail && <div className="text-xs text-[#fbbf24] flex flex-wrap items-center gap-2" data-testid="sms-upgrade-note">Email and webhook delivery are on the Pro plan and up. <PlanLock feature="SMS to email and webhooks" need="pro" phone={num.phoneNumber} testid="sms-upgrade-link" /></div>}
      <SaveRow dirty={u.dirty} testid="sms-unsaved-pill"><button className="civr-btn civr-btn-primary" disabled={busy} onClick={async () => { if (await save(f)) u.reset(); }} data-testid="sms-save-button">Save SMS settings</button></SaveRow>
    </Card>
  );
}

export function RecordingTab({ num, reload }) {
  return (
    <Card title="Call recording" sub="Record incoming calls and calls you make from the browser phone or a softphone. Recordings show up in the Logs tab.">
      <div className="flex items-center justify-between"><span className="text-sm">Record calls</span><InstantToggle num={num} feature="recording" label="Call recording" checked={!!num.features.recording} reload={reload} testid="recording-toggle" /></div>
    </Card>
  );
}
